import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function EditPromotion({ open, onClose, eventId, promotionId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(false);
    const [existingImages, setExistingImages] = useState([]);
    const modalRef = useRef(null);

    useEffect(() => {
        const fetchPromotionData = async () => {
            try {
                const response = await axios.get(`${apiUrl}event-service/promoted-event/${promotionId}`);
                if (response.data.success && response.data.data?.images) {
                    setExistingImages(response.data.data.images);
                }
            } catch (error) {
                console.error("Error fetching promotion data:", error.message);
            }
        };

        if (open) {
            fetchPromotionData();
        }
    }, [open, promotionId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        if (open) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, onClose]);

    const handleClose = () => {
        onClose();
        editPromotion.resetForm();
        setExistingImages([]);
    };

    const handleNewImagesChange = (event) => {
        const files = Array.from(event.currentTarget.files);
        const totalImages = existingImages.length + editPromotion.values.images.length;

        if (totalImages + files.length > 5) {
            editPromotion.setFieldError(
                'images',
                'You can only upload up to 5 images in total.'
            ); return;
        }

        const validFiles = files.filter(file => file.size <= 5 * 1024 * 1024); // 5MB
        const updatedFiles = [...editPromotion.values.images, ...validFiles];
        editPromotion.setFieldValue('images', updatedFiles);
    };


    const onSubmit = async (values) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('eventId', eventId);

            editPromotion.values.images.forEach((file) => {
                formData.append('images', file, file.name);
            });

            formData.append('updateImages', JSON.stringify(existingImages));

            await axios.put(`${apiUrl}event-service/promoted-event/${promotionId}`, formData, {
                headers: {
                    Authorization: `Bearer ${bearerToken}`,
                    'Content-Type': 'multipart/form-data',
                    accept: 'application/json',
                },
            });

            toast.success('Promotion has been updated!');
            setTimeout(() => handleClose(), 1000);
        } catch (error) {
            console.error('Error updating data:', error.message);
            toast.error('Unable to edit the promotion.');
        } finally {
            setIsLoading(false);
            setExistingImages([]);
        }
    };

    const editPromotion = useFormik({
        initialValues: {
            images: [],
        },
        validationSchema: Yup.object({
            images: Yup.array()
                .test(
                    "total-count",
                    "You can only upload up to 5 images in total (existing + new)",
                    function (value) {
                        const context = this.options.context || {};
                        const existing = context.existingImages?.length || 0;
                        const current = value?.length || 0;
                        return existing + current <= 5;
                    }
                )
                .test(
                    "fileSize",
                    "Each file must be less than 5MB",
                    (value) => {
                        if (!value) return true;
                        return value.every((img) => img.size <= 5 * 1024 * 1024);
                    }
                ),
        }),

        onSubmit,
        context: { existingImages }, // pass state here
    });


    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="absolute inset-0 bg-black opacity-50" onClick={onClose}></div>
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative" ref={modalRef}>
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-xl font-semibold text-center">Edit Event Promotions</h2>
                </div>

                <div className="relative bg-white p-6 rounded-b-lg shadow-md">
                    <form onSubmit={editPromotion.handleSubmit} className="space-y-6">
                        <div className="w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
                            <p className="text-xs text-gray-400 mb-2 italic">
                                You can upload new ones (up to 5, under 5MB each)
                            </p>

                            {/* Combined Image Preview */}
                            <div className="flex flex-wrap gap-4 mb-4">
                                {existingImages.map((image, index) => (
                                    <div key={`existing-${index}`} className="relative">
                                        <img
                                            src={image.url}
                                            alt={`existing-${index}`}
                                            className="w-24 h-24 object-cover rounded"
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setExistingImages((prev) => prev.filter((_, i) => i !== index))
                                            }
                                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}

                                {editPromotion.values.images.map((file, index) => (
                                    <div key={`new-${index}`} className="relative">
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={`new-${index}`}
                                            className="w-24 h-24 object-cover rounded"
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                editPromotion.setFieldValue(
                                                    'images',
                                                    editPromotion.values.images.filter((_, i) => i !== index)
                                                )
                                            }
                                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {editPromotion.errors.images && (
                                <p className="text-red-500 text-sm mt-1">{editPromotion.errors.images}</p>
                            )}

                            <label className="inline-block px-4 py-2 bg-gray-200 rounded cursor-pointer text-sm hover:bg-gray-300">
                                Upload Images
                                <input
                                    type="file"
                                    name="images"
                                    accept="image/*"
                                    multiple
                                    onChange={handleNewImagesChange}
                                    className="hidden"
                                />
                            </label>
                        </div>


                        <div className="flex justify-end mt-4">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600"
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                pauseOnHover
                theme="light"
            />
        </div>
    );
}

EditPromotion.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    eventId: PropTypes.string.isRequired,
    promotionId: PropTypes.string.isRequired,
};